var _excluded = ["children", "component", "highlightedRef", "id", "menuItemCustomProps", "onClick", "onClose", "onScroll", "role", "selectedValue"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import Scrollbar from '../scrollbar';
import useStyles from './use_styles';
import { extractOrderedValues, hasChildWithIcon } from './utils';
import propTypes from './prop_types';
import { MenuListContext } from './menu_list_context';
import { jsx as ___EmotionJSX } from "@emotion/core";
var MenuList = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var _values$;
  var {
      children,
      component = 'ul',
      highlightedRef,
      id,
      menuItemCustomProps,
      onClick,
      onClose,
      onScroll,
      role = 'menu',
      selectedValue
    } = _ref,
    menuListProps = _objectWithoutProperties(_ref, _excluded);
  var values = React.useMemo(() => extractOrderedValues(children), [children]);
  var [highlightedValue, setHighlightedValue] = React.useState((_values$ = values[0]) === null || _values$ === void 0 ? void 0 : _values$.value);
  var handleMouseDown = React.useCallback(event => event.preventDefault(), []);
  var first = React.useCallback(() => {
    setHighlightedValue(values[0].value);
  }, [values]);
  var last = React.useCallback(() => {
    setHighlightedValue(values[values.length - 1].value);
  }, [values]);
  var next = React.useCallback(() => {
    setHighlightedValue(value => {
      return values[Math.min(values.findIndex(_ref2 => {
        var {
          value: v
        } = _ref2;
        return v === value;
      }) + 1, values.length - 1)].value;
    });
  }, [values]);
  var prev = React.useCallback(() => {
    setHighlightedValue(value => {
      return values[Math.max(values.findIndex(_ref3 => {
        var {
          value: v
        } = _ref3;
        return v === value;
      }) - 1, 0)].value;
    });
  }, [values]);
  var [lastKey, setLastKey] = React.useState();
  var search = React.useCallback(key => {
    var keyLowerCase = key.toLocaleLowerCase();
    if (keyLowerCase !== lastKey) {
      var _values$find;
      setLastKey(keyLowerCase);
      setHighlightedValue((_values$find = values.find(_ref4 => {
        var {
          label,
          value
        } = _ref4;
        return (label || value.toString()).toLocaleLowerCase().startsWith(keyLowerCase);
      })) === null || _values$find === void 0 ? void 0 : _values$find.value);
    } else {
      var _values$find2;
      var currentIndex = values.findIndex(_ref5 => {
        var {
          value
        } = _ref5;
        return value === highlightedValue;
      });
      var nextItem = (_values$find2 = values.find((_ref6, i) => {
        var {
          label,
          value
        } = _ref6;
        return i > currentIndex && (label || value.toString()).toString().toLocaleLowerCase().startsWith(keyLowerCase);
      })) === null || _values$find2 === void 0 ? void 0 : _values$find2.value;
      if (nextItem === undefined) {
        var _values$find3;
        setHighlightedValue((_values$find3 = values.find(_ref7 => {
          var {
            label,
            value
          } = _ref7;
          return (label || value.toString()).toLocaleLowerCase().startsWith(keyLowerCase);
        })) === null || _values$find3 === void 0 ? void 0 : _values$find3.value);
      } else {
        setHighlightedValue(nextItem);
      }
    }
  }, [highlightedValue, lastKey, values]);
  var styles = useStyles();
  var leadingAreaWhiteSpace = React.useMemo(() => hasChildWithIcon(children), [children]);
  return ___EmotionJSX(Scrollbar, _extends({
    component: component,
    customStyle: styles,
    id: id,
    onMouseDown: handleMouseDown,
    onScroll: onScroll,
    ref: ref,
    role: role
  }, menuListProps), ___EmotionJSX(MenuListContext.Provider, {
    value: {
      selectedValue,
      highlightedValue,
      setHighlightedValue,
      highlightedRef,
      menuItemCustomProps,
      leadingAreaWhiteSpace,
      onClick,
      onClose,
      first,
      last,
      next,
      prev,
      search
    }
  }, children));
});
MenuList.propTypes = propTypes;
MenuList.displayName = 'MenuList';
export default MenuList;